import { AddCircle, Delete } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	CircularProgress,
	FormControl,
	FormGroup,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import Datagrid from "../../components/Datagrid/Datagrid";
import { UI_TEXT } from "../../constants";
import { useAuth } from "../../context/authContext";
import { getCustomersCusSites } from "../../services/manageCustomerMasterService";
import { getMachines } from "../../services/manageMachinesMasterService";
import { deleteProvisionsForCustomerSite, getProvisionedMachines, getSiteLines, provisionMachinesForCustomerSite, updateProvisionForCustomerSite } from "../../services/manageSiteMachinesService";
import { getAllCustomerSites } from "../../services/manageSitesMasterService";
import { getUserAllocations } from "../../services/manageSitesService";
import ModalWrapper from "../ModalWrapper/ModalWrapper";
import "./ManageSiteMachines.css";

const INITIAL_PROVISION_FORM = {
	machine_name: "",
	machine_number: "",
	machine_serial_number: "",
	provision_date: "",
	status: "active",
	site_name: "",
	cus_name: "",
	machine_id: "",
	site_id: "",
	cus_id: "",
	site_line_id: "",
	line_name: "",
	site_number: "",
	cus_number: "",
};

const normalizeSerial = (value = "") => value?.toString().trim().toLowerCase();
const LARGE_AUTOCOMPLETE_RESULT_LIMIT = 200;
const isSameCustomerSite = (option, value) =>
	Number(option?.site_id) === Number(value?.site_id) &&
	Number(option?.cus_id) === Number(value?.cus_id);
const normalizeAutocompleteSearch = (value = "") =>
	String(value || "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.trim();

const ManageSiteMachines = ({ refreshKey = 0 }) => {
	const { t } = useTranslation();
	// const { selectedSite } = useSite();
	const { user: authUser, hasPermission } = useAuth();
	const isSuperAdmin = Number(authUser?.user_id) === 1;
	const toBool = (value, fallback = false) => {
		if (typeof value === "boolean") return value;
		if (typeof value === "number") return value === 1;
		if (typeof value === "string") {
			const normalized = value.trim().toLowerCase();
			if (["true", "1", "yes", "on", "enabled"].includes(normalized)) return true;
			if (["false", "0", "no", "off", "disabled"].includes(normalized)) return false;
		}
		return fallback;
	};
	const [customers, setCustomers] = useState([]);
	const [sites, setSites] = useState([]);
	const [allSites, setAllSites] = useState([]);
	const [error, setError] = useState("");
	const [formErrors, setFormErrors] = useState({});
	const [loading, setLoading] = useState(true);
	const [sitesLoading, setSitesLoading] = useState(false);
	const [selectedCustomer, setSelectedCustomer] = useState(null);
	const [selectedSiteData, setSelectedSiteData] = useState(null);
	const [assignedMachines, setAssignedMachines] = useState([]);
	const [allProvisionedMachines, setAllProvisionedMachines] = useState([]);
	const [nonAssignedMachines, setNonAssignedMachines] = useState([]);
	const [newProvision, setNewProvision] = useState(false);
	const [machineSelected, setMachineSelected] = useState(null);
	const [submittingProvision, setSubmittingProvision] = useState(false);
	const [emptySites, setEmptySites] = useState(false);
	const [provisionCustomer, setProvisionCustomer] = useState(null);
	const [selectedLine, setSelectedLine] = useState(null);
	const [filterLines, setFilterLines] = useState([]);
	const [provisionSite, setProvisionSite] = useState(null);
	const [provisionLine, setProvisionLine] = useState(null);
	const [provisionSites, setProvisionSites] = useState([]);
	const [provisionLines, setProvisionLines] = useState([]);
	const [provisionFormData, setProvisionFormData] = useState(INITIAL_PROVISION_FORM);
	const [validationErrors, setValidationErrors] = useState({});
	const [errorMessage, setErrorMessage] = useState("");
	const [provisionServerError, setProvisionServerError] = useState("");
	const [selectedRows, setSelectedRows] = useState([]);
	const [editProvision, setEditProvision] = useState(false);
	const [editSubmitting, setEditSubmitting] = useState(false);
	const [editFormData, setEditFormData] = useState({
		id: "",
		machine_name: "",
		machine_number: "",
		machine_serial_number: "",
		status: t("MENU_ITEMS.ACTIVE"),
	});
	const [editValidationErrors, setEditValidationErrors] = useState({});
	const [editError, setEditError] = useState("");
	const [fallbackActorSiteIds, setFallbackActorSiteIds] = useState([]);
	const customerSiteSearchCacheRef = useRef(new WeakMap());

	const actorRoleType = useMemo(
		() => String(authUser?.role?.role_type || authUser?.role?.role_name || "").trim().toLowerCase(),
		[authUser?.role?.role_type, authUser?.role?.role_name]
	);
	const actorSiteIds = useMemo(() => {
	const fromAllocs = Array.isArray(authUser?.allocations)
		? authUser.allocations.map((a) => ({
			site_id: Number(a?.site_id),
			cus_id: Number(a?.cus_id),
		}))
		: [];

	return fromAllocs;
	}, [authUser?.allocations]);
	const effectiveActorSiteIds = useMemo(
		() => (actorSiteIds.length > 0 ? actorSiteIds : fallbackActorSiteIds),
		[actorSiteIds, fallbackActorSiteIds]
	);
	const actorHasAllSiteVisibility = useMemo(
		() =>
			Number(authUser?.user_id) === 1 ||
			((actorRoleType === "admin" || toBool(authUser?.role?.permissions?.machine_provision_management?.manage_all_sites, false)) && effectiveActorSiteIds.length === 0),
		[authUser?.user_id, actorRoleType, authUser?.role?.permissions?.machine_provision_management?.manage_all_sites, effectiveActorSiteIds.length]
	);
	const customerSiteFilterOptions = useCallback((options = [], { inputValue } = {}) => {
		const normalizedInput = normalizeAutocompleteSearch(inputValue);
		if (!normalizedInput) return options.slice(0, LARGE_AUTOCOMPLETE_RESULT_LIMIT);
		const filtered = [];
		for (const option of options) {
			let searchable = customerSiteSearchCacheRef.current.get(option);
			if (searchable === undefined) {
				searchable = normalizeAutocompleteSearch(
					`${option?.customer_name || ""} ${option?.customer_number || ""} ${option?.customer_site_name || ""} ${option?.customer_site_number || ""}`
				);
				customerSiteSearchCacheRef.current.set(option, searchable);
			}
			if (searchable.includes(normalizedInput)) filtered.push(option);
			if (filtered.length >= LARGE_AUTOCOMPLETE_RESULT_LIMIT) break;
		}
		return filtered;
	}, []);


	const normalizeProvisionRow = (row = {}) => ({
		machine_id: row.machine_id,
		machine_name: row.machine_name,
		machine_number: row.machine_number,
		sm_machine_product_name: row.machine_product_name || row.sm_machine_product_name,
		sm_machine_status: row.machine_status || row.sm_machine_status,
		sm_machine_id: row.id || row.sm_machine_id,
		site_machine_id: row.site_machine_id,
		provision_row_id: row.site_machine_id || row.provision_row_id || row.id || row.sm_machine_id,
		customer_number: row.customer_number || row.cus_number,
		customer_name: row.customer_name || row.cus_name,
		customer_site_number: row.customer_site_number || row.site_number,
		customer_site_name: row.customer_site_name || row.site_name,
		site_id: row.site_id,
		cus_id: row.cus_id,
		site_line_id: row.site_line_id,
		line_id: row.line_id,
		line_name: row.line_name,
		line_description: row.line_description,
	});

	// const applyProvisionFilters = (provisions, customer, site) => {
	// 	if (site?.site_id) {
	// 		return provisions.filter((row) => row.site_id === site.site_id);
	// 	}
	// 	if (customer?.cus_id) {
	// 		return provisions.filter((row) => row.cus_id === customer.cus_id);
	// 	}
	// 	return provisions;
	// };
	const applyProvisionFilters = (provisions, customer, site, line = null) => {
			let result = provisions;
			if (customer?.cus_id) {
				result = result.filter((row) => Number(row.cus_id) === Number(customer.cus_id));
			}
			if (site?.site_id) {
				result = result.filter((row) => Number(row.site_id) === Number(site.site_id));
			}
			if (line?.site_line_id) {
				result = result.filter((row) => Number(row.site_line_id) === Number(line.site_line_id));
			}
			return result;
		};

		const resetProvisionForm = () => {
			setProvisionFormData(INITIAL_PROVISION_FORM);
			setValidationErrors({});
			setErrorMessage("");
			setProvisionServerError("");
			setMachineSelected(null);
			setProvisionCustomer(null);
			setProvisionSite(null);
			setProvisionLine(null);
			setProvisionSites([]);
			setProvisionLines([]);
		};

	// Handle change function for all fields
	const handleProvisionFormChange = (event) => {
		const { name, value } = event.target;
		setProvisionFormData((prevData) => ({
			...prevData,
			[name]: value,
		}));
		setValidationErrors((prev) => ({ ...prev, [name]: "" }));
		if (errorMessage) setErrorMessage("");
	};

	useEffect(() => {
		let mounted = true;
		const loadFallbackActorScope = async () => {
			if (Number(authUser?.user_id) === 1 || actorSiteIds.length > 0 || !authUser?.user_id) {
				if (mounted) setFallbackActorSiteIds([]);
				return;
			}
			try {
				const resp = await getUserAllocations(authUser.user_id);
				const allocations = Array.isArray(resp?.data?.allocations) ? resp.data.allocations : [];
				const ids = Array.from(
					new Set(
						allocations
							.map((a) => ({
								site_id: Number(a?.site_id),
								cus_id: Number(a?.cus_id),
								}))
							.filter((id) =>
									Number.isFinite(id.site_id) &&
									id.site_id > 0 &&
									Number.isFinite(id.cus_id) &&
									id.cus_id > 0
								)
					)
				);
				if (mounted) setFallbackActorSiteIds(ids);
			} catch (_err) {
				if (mounted) setFallbackActorSiteIds([]);
			}
		};
		loadFallbackActorScope();
		return () => { mounted = false; };
	}, [actorSiteIds, authUser?.user_id]);

	useEffect(() => {
		const fetchInitialData = async () => {
			try {
				const [responseCustomers, responseSites, responseProvisioned] = await Promise.all([
					getCustomersCusSites(),
					getAllCustomerSites(),
					getProvisionedMachines({ includeInactive: true }),
				]);

				console.log("In managesitemachines getcustomercussites response", responseCustomers);
				if (responseCustomers.status) {
					const filteredCustomers = responseCustomers.data.filter(
						(customer) => customer.customer_name && customer.customer_name !== "EMPTY"
					);
					setCustomers(filteredCustomers);
				} else {
					setError(t('GENERAL.FAILED_TO_FETCH_CUSTOMERS'));
				}

				if (responseSites.status) {
					setAllSites(responseSites.data);
					setSites(responseSites.data);
				}

				if (responseProvisioned.status) {
					const normalized = responseProvisioned.data.map(normalizeProvisionRow);
					setAllProvisionedMachines(normalized);
					setAssignedMachines(normalized);
				}
			} catch (err) {
				setError(t('GENERAL.FAILED_TO_FETCH_CUSTOMERS_1') + " " + err.message);
			} finally {
				setLoading(false);
			}
		};

		fetchInitialData();
	}, [t]);

	const scopedSites = useMemo(() => {
		if (actorHasAllSiteVisibility) return allSites || [];
		const allowedSet = new Set((effectiveActorSiteIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0));
		return allSites.filter(site =>
			effectiveActorSiteIds.some(
				(alloc) =>
				Number(alloc.site_id) === Number(site.site_id) &&
				Number(alloc.cus_id) === Number(site.cus_id)
			)
			);
	}, [actorHasAllSiteVisibility, allSites, effectiveActorSiteIds]);
	// const scopedSiteIdSet = useMemo(
	// 	() => new Set((scopedSites || []).map((site) => Number(site?.site_id)).filter((id) => Number.isFinite(id) && id > 0)),
	// 	[scopedSites]
	// );
	const scopedAllProvisionedMachines = useMemo(() => {
		if (actorHasAllSiteVisibility) return allProvisionedMachines || [];
  		return (allProvisionedMachines || []).filter((row) =>
    effectiveActorSiteIds.some(
      (alloc) =>
        Number(alloc.site_id) === Number(row.site_id) &&
        Number(alloc.cus_id) === Number(row.cus_id)
    )
  );
}, [actorHasAllSiteVisibility, allProvisionedMachines, effectiveActorSiteIds]);
	const scopedCustomers = useMemo(() => {
		if (actorHasAllSiteVisibility) return customers || [];
		const allowedCustomerIds = new Set(
			(scopedSites || [])
				.map((site) => Number(site?.cus_id))
				.filter((id) => Number.isFinite(id) && id > 0)
		);
		return (customers || []).filter((customer) => allowedCustomerIds.has(Number(customer?.cus_id)));
	}, [actorHasAllSiteVisibility, customers, scopedSites]);

	useEffect(() => {
		if (selectedCustomer && !scopedCustomers.some((customer) => Number(customer?.cus_id) === Number(selectedCustomer?.cus_id))) {
			setSelectedCustomer(null);
			setSelectedSiteData(null);
		}
	}, [selectedCustomer, scopedCustomers]);

	useEffect(() => {
		if (selectedSiteData && !scopedSites.some((site) => isSameCustomerSite(site, selectedSiteData))) {
			setSelectedSiteData(null);
		}
	}, [selectedSiteData, scopedSites]);

	useEffect(() => {
		setAssignedMachines(applyProvisionFilters(scopedAllProvisionedMachines, selectedCustomer, selectedSiteData, selectedLine));
	}, [scopedAllProvisionedMachines, selectedCustomer, selectedSiteData, selectedLine]);

	useEffect(() => {
		if (selectedCustomer) {
			const filtered = scopedSites.filter((site) => Number(site?.cus_id) === Number(selectedCustomer?.cus_id));
			setSites(filtered);
			setEmptySites(filtered.length === 0);
			return;
		}
		setSites(scopedSites);
		setEmptySites(false);
	}, [scopedSites, selectedCustomer]);

	useEffect(() => {
		setSelectedLine(null);
		setFilterLines([]);
		if (!selectedSiteData?.site_id) return;
		getSiteLines({
			site_id: selectedSiteData.site_id,
			cus_id: selectedSiteData.cus_id || selectedCustomer?.cus_id,
		}).then((response) => {
			setFilterLines(response?.status ? response.data || [] : []);
		}).catch(() => setFilterLines([]));
	}, [selectedSiteData]);

	useEffect(() => {
		if (provisionCustomer) {
			setProvisionSites(scopedSites.filter((site) => Number(site?.cus_id) === Number(provisionCustomer?.cus_id)));
			return;
		}
		setProvisionSites(scopedSites);
	}, [scopedSites, provisionCustomer]);

	// const scopedSites = useMemo(() => {
	// 	if (actorHasAllSiteVisibility) return allSites || [];
	// 	const allowedSet = new Set((effectiveActorSiteIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0));
	// 	return (allSites || []).filter((site) => allowedSet.has(Number(site?.site_id)));
	// }, [actorHasAllSiteVisibility, allSites, effectiveActorSiteIds]);
	const scopedSiteIdSet = useMemo(
		() => new Set((scopedSites || []).map((site) => Number(site?.site_id)).filter((id) => Number.isFinite(id) && id > 0)),
		[scopedSites]
	);
	// const scopedAllProvisionedMachines = useMemo(() => {
	// 	if (actorHasAllSiteVisibility) return allProvisionedMachines || [];
	// 	return (allProvisionedMachines || []).filter((row) => scopedSiteIdSet.has(Number(row?.site_id)));
	// }, [actorHasAllSiteVisibility, allProvisionedMachines, scopedSiteIdSet]);
	// const scopedCustomers = useMemo(() => {
	// 	if (actorHasAllSiteVisibility) return customers || [];
	// 	const allowedCustomerIds = new Set(
	// 		(scopedSites || [])
	// 			.map((site) => Number(site?.cus_id))
	// 			.filter((id) => Number.isFinite(id) && id > 0)
	// 	);
	// 	return (customers || []).filter((customer) => allowedCustomerIds.has(Number(customer?.cus_id)));
	// }, [actorHasAllSiteVisibility, customers, scopedSites]);

	useEffect(() => {
		if (selectedCustomer && !scopedCustomers.some((customer) => Number(customer?.cus_id) === Number(selectedCustomer?.cus_id))) {
			setSelectedCustomer(null);
			setSelectedSiteData(null);
			setAssignedMachines(applyProvisionFilters(scopedAllProvisionedMachines, null, null));
		}
	}, [selectedCustomer, scopedCustomers, scopedAllProvisionedMachines]);

	useEffect(() => {
		if (selectedSiteData && !scopedSites.some((site) => Number(site?.site_id) === Number(selectedSiteData?.site_id))) {
			setSelectedSiteData(null);
		}
	}, [selectedSiteData, scopedSites]);

	useEffect(() => {
		setAssignedMachines(applyProvisionFilters(scopedAllProvisionedMachines, selectedCustomer, selectedSiteData));
	}, [scopedAllProvisionedMachines, selectedCustomer, selectedSiteData]);

	useEffect(() => {
		if (selectedCustomer) {
			const filtered = scopedSites.filter((site) => Number(site?.cus_id) === Number(selectedCustomer?.cus_id));
			setSites(filtered);
			setEmptySites(filtered.length === 0);
			return;
		}
		setSites(scopedSites);
		setEmptySites(false);
	}, [scopedSites, selectedCustomer]);

	useEffect(() => {
		if (provisionCustomer) {
			setProvisionSites(scopedSites.filter((site) => Number(site?.cus_id) === Number(provisionCustomer?.cus_id)));
			return;
		}
		setProvisionSites(scopedSites);
	}, [scopedSites, provisionCustomer]);

	const filterSitesForCustomer = (customer) => {
		setSitesLoading(true);
		if (!customer) {
			setSites(scopedSites);
			setEmptySites(false);
			setSitesLoading(false);
			return;
		}

		const filtered = scopedSites.filter((site) => Number(site?.cus_id) === Number(customer?.cus_id));
		setSites(filtered);
		setEmptySites(filtered.length === 0);
		setSitesLoading(false);
	};

	const getCustomerSiteOptionLabel = (option) => {
		if (!option) return t('UI_TEXT.NO_SITES_AVAILABLE');
		const siteLabel = `${option.customer_site_name || ""} - ${option.customer_site_number || ""}`.trim();
		if (!selectedCustomer && option.customer_name) {
			return `${siteLabel} (${option.customer_name})`;
		}
		return siteLabel || t('UI_TEXT.NO_SITES_AVAILABLE');
	};

	const getProvisionSiteOptionLabel = (option) => {
		if (!option) return t('UI_TEXT.NO_SITES_AVAILABLE');
		const siteLabel = `${option.customer_site_name || ""} - ${option.customer_site_number || ""}`.trim();
		if (!provisionCustomer && option.customer_name) {
			return `${siteLabel} (${option.customer_name})`;
		}
		return siteLabel || t('UI_TEXT.NO_SITES_AVAILABLE');
	};

		const handleEditSite = async (selectedSiteData) => {
		try {
			// Fetch all machines
			const respMachines = await getMachines();
			const allMachines = respMachines.data;

				setAssignedMachines(
					applyProvisionFilters(scopedAllProvisionedMachines, selectedCustomer, selectedSiteData)
				);
			setNonAssignedMachines(allMachines);

			// Do something with filterMachines and nonProvisionedMachines as needed
		} catch (error) {
			console.error("Error fetching machines: ", error);
		}
	};

	const loadMachinesForProvision = async () => {
		try {
			const respMachines = await getMachines();
			setNonAssignedMachines(respMachines.data || []);
		} catch (error) {
			console.error("Error fetching machines: ", error);
		}
	};

	const updateProvisionSites = (customer) => {
		if (!customer) {
			setProvisionSites(scopedSites);
			return;
		}
		const filtered = scopedSites.filter((site) => Number(site?.cus_id) === Number(customer?.cus_id));
		setProvisionSites(filtered);
	};

	const refreshProvisionedMachines = useCallback(async () => {
		try {
			const responseProvisioned = await getProvisionedMachines({ includeInactive: true });
			if (!responseProvisioned.status) {
				setError(t('GENERAL.FAILED_TO_FETCH_CUSTOMERS'));
				return;
			}
			const normalized = responseProvisioned.data.map(normalizeProvisionRow);
			setAllProvisionedMachines(normalized);
		} catch (err) {
			setError(t('GENERAL.FAILED_TO_FETCH_CUSTOMERS_1') + " " + err.message);
		}
	}, [t]);

	useEffect(() => {
		if (!refreshKey) return;
		refreshProvisionedMachines();
	}, [refreshKey, refreshProvisionedMachines]);

	const loadProvisionLines = async (site, customer = provisionCustomer) => {
		setProvisionLine(null);
		setProvisionLines([]);
		setProvisionFormData((prev) => ({ ...prev, site_line_id: "", line_name: "" }));
		if (!site?.site_id) return;

		try {
			const response = await getSiteLines({
				site_id: site.site_id,
				cus_id: customer?.cus_id || site?.cus_id,
			});
			const lines = response?.status ? response.data || [] : [];
			setProvisionLines(lines);
			if (lines.length === 1) {
				setProvisionLine(lines[0]);
				setProvisionFormData((prev) => ({
					...prev,
					site_line_id: lines[0].site_line_id || "",
					line_name: lines[0].line_name || "",
				}));
			}
		} catch (error) {
			console.error("Error fetching site lines: ", error);
		}
	};

	const handleProvisionFormSubmit = async () => {
		setSubmittingProvision(true);
		const requiredFields = [
			{ field: "machine_name", label: t('TABLE_HEADERS.MACHINE_NAME') },
			{ field: "machine_serial_number", label: t('LABELS.MACHINE_SERIAL_NUMBER') },
			{ field: "provision_date", label: t('LABELS.PROVISION_DATE') },
		];

		const nextErrors = {};
		requiredFields.forEach(({ field, label }) => {
			if (!`${provisionFormData[field] || ""}`.trim()) {
				nextErrors[field] = `${label} is required`;
			}
		});
		if (!provisionCustomer) {
			nextErrors.cus_id = t('LABELS.SELECT_A_CUSTOMER');
		}
		if (!provisionSite) {
			nextErrors.site_id = t('LABELS.SELECT_CUSTOMER_SITE');
		}
		if (!provisionLine) {
			nextErrors.site_line_id = "Select a line";
		}

		const newSerial = normalizeSerial(provisionFormData.machine_serial_number);
		const duplicateComposite = scopedAllProvisionedMachines.some(
			(machine) =>
				Number(machine.cus_id) === Number(provisionFormData.cus_id) &&
				Number(machine.site_id) === Number(provisionFormData.site_id) &&
				Number(machine.site_line_id) === Number(provisionFormData.site_line_id) &&
				machine.machine_number === provisionFormData.machine_number &&
				normalizeSerial(machine.sm_machine_product_name) === newSerial
		);
		if (duplicateComposite) {
			nextErrors.machine_serial_number = t('GENERAL.THIS_MACHINE_WITH_THE_SAME_MACHINE_SERIAL_ALREADY_EXISTS');
		}

		if (Object.keys(nextErrors).length > 0) {
			setValidationErrors(nextErrors);
			setErrorMessage(t('UI_TEXT.PLEASE_FIX_THE_HIGHLIGHTED_FIELDS'));
			setSubmittingProvision(false);
			return;
		}

		setValidationErrors({});
		setErrorMessage("");
		setProvisionServerError("");

		const req_body = [
			{
				MachineId: provisionFormData.machine_id,
				MachineNumber: provisionFormData.machine_number,
				MachineName: provisionFormData.machine_name,
				MachineSerialNo: provisionFormData.machine_serial_number,
				CustomerNumber: provisionFormData.cus_number,
				SiteNumber: provisionFormData.site_number,
				CustomerId: provisionFormData.cus_id,
				SiteId: provisionFormData.site_id,
				SiteLineId: provisionFormData.site_line_id,
				Status: provisionFormData.status,
			},
		];

		try {
			const responseProvision = await provisionMachinesForCustomerSite(req_body);
			console.log("In managesitemachines provisionmachinesforcussite response", responseProvision);
			setSubmittingProvision(false);

			const results = responseProvision.data;
			const successResults = [];
			const errorResults = [];
			const existsResults = [];

			// Categorize results by status
			results.forEach((result) => {
				if (result.status === "inserted") {
					const data = {
						machine_id: result.data.machine_id,
						machine_name: result.data.machine_name,
						sm_machine_product_name: result.data.machine_product_name,
						machine_number: result.data.machine_number,
						sm_machine_status: result.data.machine_status,
						sm_machine_id: result.data.id,
						site_machine_id: result.data.site_machine_id,
						provision_row_id: result.data.site_machine_id || result.data.id,
						customer_number: provisionCustomer?.customer_number || "",
						customer_name: provisionCustomer?.customer_name || "",
						customer_site_number: provisionSite?.customer_site_number || "",
						customer_site_name: provisionSite?.customer_site_name || "",
						site_id: provisionSite?.site_id,
						cus_id: provisionCustomer?.cus_id,
						site_line_id: provisionLine?.site_line_id,
						line_id: provisionLine?.line_id,
						line_name: provisionLine?.line_name,
					};

					setAssignedMachines((prev) => {
						return [...prev, data]; // This will append the new 'data' object to the previous array
					});
					setAllProvisionedMachines((prev) => [...prev, data]);
					successResults.push(result);
				} else if (result.status === "error") {
					errorResults.push(result);
				} else if (result.status === "exists") {
					existsResults.push(result);
				}
			});

			if (existsResults.length > 0) {
				setValidationErrors((prev) => ({
					...prev,
					machine_name: t('GENERAL.MACHINE_ALREADY_PROVISIONED_FOR_THIS_SITE'),
				}));
				setProvisionServerError(
					`Machine ${provisionFormData.machine_number} ${t('GENERAL.IS_ALREADY_PROVISIONED_FOR_THIS_SITE_PLEASE_REMOVE_OR_EDIT_THE_EXISTING_PROVISION_BEFORE_ADDING_IT_AGAIN')}`
				);
				return;
			}

			// Construct the success message
			let successMessage = "";
			if (successResults.length > 0) {
				successMessage += `<b>${successResults.length}</b> ${t('GENERAL.MACHINES_SUCCESSFULLY_PROVISIONEDBR')}`;
				successResults.forEach((result, index) => {
					successMessage += `${index + 1}. Machine <b>${result.data.machine_name}</b> provisioned to <b>${
						provisionFormData.site_name
					}</b>.<br>`;
				});
			}

			const getResultMachineLabel = (result) =>
				result.data?.machine_name ||
				result.data?.MachineName ||
				result.data?.machine_number ||
				result.data?.MachineNumber ||
				"Unknown";

			// Construct the error message
			let errorMessage = "";
			if (errorResults.length > 0) {
				errorMessage += `<b>${errorResults.length}</b> ${t('GENERAL.MACHINES_FAILED_TO_PROVISION_DUE_TO_ERRORSBR')}`;
				errorResults.forEach((result, index) => {
					const detail = result.error && result.error !== result.message ? ` (${result.error})` : "";
					errorMessage += `${index + 1}. Machine <b>${getResultMachineLabel(result)}</b>: ${
						result.message
					}${detail}<br>`;
				});
			}

			// Construct the exists message
			let existsMessage = "";
			if (existsResults.length > 0) {
				existsMessage += `<b>${existsResults.length}</b> ${t('GENERAL.MACHINES_ALREADY_EXIST_IN_THE_SYSTEMBR')}`;
				existsResults.forEach((result, index) => {
					existsMessage += `${index + 1}. Machine <b>${getResultMachineLabel(result)}</b>: ${
						result.message
					}<br>`;
				});
			}

			// Display Swal
			Swal.fire({
				icon: successResults.length > 0 ? "success" : "info",
				title: t('TITLES.PROVISIONING_RESULTS'),
				html: `${successMessage}${errorMessage}${existsMessage}`,
			});

			// Reset the form only if provisioning was successful
			if (successResults.length > 0) {
				setProvisionFormData({
					machine_name: "",
					machine_number: "",
					machine_serial_number: "",
					provision_date: "",
					status: "active",
					site_name: "",
					cus_name: "",
					site_id: "",
					cus_id: "",
					site_line_id: "",
					line_name: "",
					machine_id: "",
					site_number: "",
					cus_number: "",
				});
				setNewProvision(false);
				setMachineSelected(null);
			}
		} catch (error) {
			console.error(error);
			Swal.fire({
				icon: "error",
				title: t('ALERT_MESSAGES.ERROR'),
				text: t('ALERT_MESSAGES.PLEASE_TRY_AGAIN'),
			});
		}

		// Proceed with your API call or logic
	};

	const handleDeleteProvisions = async () => {
		try {
			const confirmation = await Swal.fire({
				title: t('ALERT_MESSAGES.ARE_YOU_SURE'),
				text: t('GENERAL.THIS_WILL_DELETE_THE_PROVISIONS_PERMANENTLY'),
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: "#d33",
				cancelButtonColor: "#3085d6",
				confirmButtonText:  t('GENERAL.YES_DELETE_IT'),
			});

			if (!confirmation.isConfirmed) return;

			const req_body = {
				sm_ids: selectedRows,
			};
			const response = await deleteProvisionsForCustomerSite(req_body);
			console.log("In managesitemachines deleteprovisionsforcussite response", response);
			if (response.status) {
				setAssignedMachines((prevList) => prevList.filter((row) => !selectedRows.includes(row.provision_row_id)));
				setAllProvisionedMachines((prevList) => prevList.filter((row) => !selectedRows.includes(row.provision_row_id)));
				Swal.fire({
					icon: "success",
					title: t('ALERT_MESSAGES.SUCCESS'),
					html: t('ALERT_MESSAGES.SUCCFULLY_MESSAGE'),
				});
			}
		} catch (error) {
			console.log(error);
		}
	};

	const handleRowSelect = (rows) => {
		setSelectedRows(rows);
	};

	const openEditModalFromRow = (row) => {
		setEditValidationErrors({});
		setEditError("");
		setEditFormData({
			id: row.sm_machine_id,
			machine_name: row.machine_name || "",
			machine_number: row.machine_number || "",
			line_name: row.line_name || "",
			machine_serial_number: row.sm_machine_product_name || "",
			status: row.sm_machine_status || t("MENU_ITEMS.ACTIVE"),
			cus_name: row.customer_name || selectedCustomer?.customer_name || "",
			site_name: row.customer_site_name || selectedSiteData?.customer_site_name || "",
		});
		setEditProvision(true);
	};

	const deleteSingleProvision = async (row) => {
		try {
			const confirmation = await Swal.fire({
				title: t('ALERT_MESSAGES.ARE_YOU_SURE'),
				text: t('GENERAL.THIS_WILL_DELETE_THE_PROVISION_PERMANENTLY'),
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: "#d33",
				cancelButtonColor: "#3085d6",
				confirmButtonText:  t('GENERAL.YES_DELETE_IT'),
			});

			if (!confirmation.isConfirmed) return;

			const response = await deleteProvisionsForCustomerSite({ sm_ids: [row.provision_row_id] });
			console.log("In managesitemachines delproviforcussite response", response);
			if (response.status) {
				setAssignedMachines((prevList) => prevList.filter((r) => r.provision_row_id !== row.provision_row_id));
				setAllProvisionedMachines((prevList) => prevList.filter((r) => r.provision_row_id !== row.provision_row_id));
				Swal.fire({ icon: "success", title: t('ALERT_MESSAGES.SUCCESS'), html: t('ALERT_MESSAGES.SUCCFULLY_MESSAGE') });
			}
		} catch (error) {
			console.log(error);
		}
	};

	const handleEditFieldChange = (e) => {
		const { name, value } = e.target;
		setEditFormData((p) => ({ ...p, [name]: value }));
		if (name === "machine_serial_number") {
			setEditValidationErrors((prev) => ({ ...prev, machine_serial_number: "" }));
			if (editError) setEditError("");
		}
	};

	const submitEditProvision = async () => {
		try {
			setEditSubmitting(true);
			const nextErrors = {};
			const normalizedSerial = normalizeSerial(editFormData.machine_serial_number);
			if (!normalizedSerial) {
				nextErrors.machine_serial_number = t('GENERAL.MACHINE_SERIAL_IS_REQUIRED');
			} else {
				const conflict = assignedMachines.some(
					(machine) =>
						machine.sm_machine_id !== editFormData.id &&
						machine.machine_number === editFormData.machine_number &&
						normalizeSerial(machine.sm_machine_product_name) === normalizedSerial
				);
				if (conflict) {
					nextErrors.machine_serial_number = t('GENERAL.THIS_MACHINE_WITH_THE_SAME_MACHINE_SERIAL_ALREADY_EXISTS');
				}
			}

			if (Object.keys(nextErrors).length > 0) {
				setEditValidationErrors(nextErrors);
				setEditError('Please fix the highlighted fields.');
				setEditSubmitting(false);
				return;
			}

			setEditValidationErrors({});
			setEditError("");

			const payload = {
				machine_status: editFormData.status,
				machine_product_name: editFormData.machine_serial_number,
				// machine_name can be enabled later if needed
			};
			const resp = await updateProvisionForCustomerSite(editFormData.id, payload);
			console.log("In managesitemachines updateprovisionforcussite response", resp);
			if (resp.status) {
				// Update local grid row
				setAssignedMachines((prev) => prev.map((r) => {
					if (r.sm_machine_id === editFormData.id) {
						return {
							...r,
							sm_machine_status: editFormData.status,
							sm_machine_product_name: editFormData.machine_serial_number,
							machine_name: editFormData.machine_name,
						};
					}
					return r;
				}));
				setAllProvisionedMachines((prev) => prev.map((r) => {
					if (r.sm_machine_id === editFormData.id) {
						return {
							...r,
							sm_machine_status: editFormData.status,
							sm_machine_product_name: editFormData.machine_serial_number,
							machine_name: editFormData.machine_name,
						};
					}
					return r;
				}));
				Swal.fire({ icon: "success", title: t('ALERT_MESSAGES.SUCCESS'), text: t('GENERAL.PROVISION_UPDATED_SUCCESSFULLY') });
				setEditProvision(false);
			} else {
				Swal.fire({ icon: "error", title: t('ALERT_MESSAGES.ERROR'), text: resp?.data?.message || t('GENERAL.FAILED_TO_UPDATE_PROVISION') });
			}
		} catch (err) {
			console.error(err);
			Swal.fire({ icon: "error", title: t('ALERT_MESSAGES.ERROR'), text: t('GENERAL.SOMETHING_WENT_WRONG_WHILE_UPDATING_THE_PROVISION') });
		} finally {
			setEditSubmitting(false);
		}
	};

	const customButtons = [
		...((isSuperAdmin || hasPermission('machine_provision_management.provision_machines')) ? [{
			label: t('LABELS.ADD_NEW_PROVISION'),
			icon: <AddCircle />,
			onClick: () => {
				resetProvisionForm();
				setProvisionCustomer(selectedCustomer || null);
				setProvisionSite(selectedSiteData || null);
				updateProvisionSites(selectedCustomer || null);
				if (selectedSiteData) {
					loadProvisionLines(selectedSiteData, selectedCustomer || selectedSiteData);
				}
				loadMachinesForProvision();
				if (selectedCustomer) {
					setProvisionFormData((prev) => ({
						...prev,
						cus_id: selectedCustomer.cus_id,
						cus_number: selectedCustomer.customer_number,
						cus_name: selectedCustomer.customer_name,
					}));
				}
				if (selectedSiteData) {
					setProvisionFormData((prev) => ({
						...prev,
						site_id: selectedSiteData.site_id,
						site_number: selectedSiteData.customer_site_number,
						site_name: selectedSiteData.customer_site_name,
					}));
				}
				setNewProvision(true);
			},
		}] : []),
		...((isSuperAdmin || hasPermission('machine_provision_management.delete_provisions')) ? [{
			label: t('LABELS.DELETE_SELECTED'),
			color: "error",
			icon: <Delete />,
			onClick: () => {
				handleDeleteProvisions();
			},
			disabled: selectedRows.length === 0,
		}] : [])

	];

	const columns = [
		{ field: "customer_number", headerName: t('TABLE_HEADERS.CUSTOMER_NUMBER'), width: 160 },
		{ field: "customer_site_number", headerName: t('TABLE_HEADERS.SITE_NUMBER'), width: 160 },
		{ field: "customer_site_name", headerName: t('LABELS.SITE_NAME'), flex: 1 },
		{ field: "line_name", headerName: "Line", width: 160 },
		{ field: "machine_name", headerName: t('TABLE_HEADERS.MACHINE_NAME'), flex: 1 },
		{
			field: "sm_machine_product_name",
			headerName:t('LABELS.MACHINE_SERIAL'),
			width: 200,
			align: "center",
			headerAlign: "center",
			renderCell: (params) => {
				const statusText =
					params.row.sm_machine_product_name === "EMPTY"
						? `<span style="font-size: 10px; color: #aaa">NA</span>`
						: params.row.sm_machine_product_name;
				return <span dangerouslySetInnerHTML={{ __html: statusText }} />;
			},
		},
		{
			field: "machine_number",
			headerName:t('TABLE_HEADERS.MACHINE_NUMBER'),
			width: 260,
			align: "center",
			headerAlign: "center",
			renderCell: (params) => params.row.machine_number,
		},
		{
			field: "sm_machine_status",
			headerName: t('TABLE_HEADERS.MACHINE_STATUS'),
			width: 200,
			align: "center",
			headerAlign: "center",
		},
		{
			field: "actions",
			headerName: t('TABLE_HEADERS.ACTIONS'),
			width: 100,
			headerAlign: 'center',
			align: 'center',
			renderCell: (params) => {
				const canEdit = isSuperAdmin || hasPermission('machine_provision_management.edit_provisions');
				const canDelete = isSuperAdmin || hasPermission('machine_provision_management.delete_provisions');
				return (
					<>
						<Box sx={{ display: "flex", flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>

							<Box sx={{ display: "flex", justifyContent: 'center', alignItems: 'center' }}>

								<IconButton color="primary" disabled={!canEdit} onClick={() => openEditModalFromRow(params.row)}>
									<EditIcon />
								</IconButton>
								<IconButton color="error" disabled={!canDelete} onClick={() => deleteSingleProvision(params.row)}>
									<Delete />
								</IconButton>
							</Box>
						</Box>

					</>
				)
			},
		},

	];

	if (loading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "40vh" }}>
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return (
			<Alert severity="error" className="error-alert">
				{error}
			</Alert>
		);
	}

	let label = t('LABELS.CUSTOMER_VIEW_LOCATIONS');

	// Check if siteLoading is true

	if (emptySites) {
		label = t('LABELS.NO_SITES_FOUND_CUSTOMER');
	} else if (!sitesLoading && sites.length === 0) {
		label = t('LABELS.CUSTOMER_VIEW_LOCATIONS');
	} else if (sitesLoading) {
		label =t('LABELS.LOADING');
	} else if (!sitesLoading && sites.length > 0) {
		label = t('LABELS.SELECT_CUSTOMER_SITE');
	}

	return (
		<Box>
			<Box
				sx={{
					mb: 2,
					mt: 1,
					display: "flex",
					flexDirection: "column",
					rowGap: '5px',
					background: '#eee',
					p: 2,
					borderRadius: '10px'
				}}>
				<Typography variant="body1" fontWeight={"bold"}>Filters: </Typography>
				<Box
					sx={{
						display: "flex",
					flexDirection: "row",
					columnGap: "20px",
				}}>

					<Autocomplete
						options={scopedCustomers}
					getOptionLabel={(option) => option.customer_name}
					value={selectedCustomer}
					isOptionEqualToValue={(option, value) => Number(option?.cus_id) === Number(value?.cus_id)}
						onChange={(event, newValue) => {
							setSelectedCustomer(newValue);
							setSelectedSiteData(null);
							setSelectedLine(null);
							setFilterLines([]);
							setAssignedMachines(applyProvisionFilters(scopedAllProvisionedMachines, newValue, null));
							setNonAssignedMachines([]);
						resetProvisionForm();
						if (newValue) {
							filterSitesForCustomer(newValue);
						} else {
							filterSitesForCustomer(null);
							setEmptySites(false);
						}
					}}
					filterOptions={customerSiteFilterOptions}
					renderInput={(params) => <TextField {...params} label={t('LABELS.SELECT_A_CUSTOMER')} variant="outlined" />}
					fullWidth
					size="small"
				/>

					<Autocomplete
						options={sites}
					getOptionLabel={getCustomerSiteOptionLabel}
					value={selectedSiteData}
					isOptionEqualToValue={isSameCustomerSite}
					onChange={(event, newValue) => {
						setSelectedSiteData(newValue);
						if (newValue) {
							const parentCustomer = scopedCustomers.find((customer) => Number(customer?.cus_id) === Number(newValue?.cus_id));
								if (parentCustomer) {
									setSelectedCustomer(parentCustomer);
									filterSitesForCustomer(parentCustomer);
								}
								setAssignedMachines(applyProvisionFilters(scopedAllProvisionedMachines, parentCustomer || selectedCustomer, newValue));
								handleEditSite(newValue);
							} else {
								setAssignedMachines(applyProvisionFilters(scopedAllProvisionedMachines, selectedCustomer, null));
								setNonAssignedMachines([]);
								setSelectedSiteData(null);
							}
					}}
					filterOptions={customerSiteFilterOptions}
					renderInput={(params) => (
						<TextField
							{...params}
							label={label}
							variant="outlined"
							placeholder={sitesLoading ? t('LABELS.LOADING') : t('LABELS.CUSTOMER_VIEW_LOCATIONS')}
						/>
					)}
					fullWidth
					size="small"
						disabled={sitesLoading || sites.length === 0}
				/>

					<Autocomplete
						options={filterLines}
					getOptionLabel={(option) => option?.line_name || ""}
					value={selectedLine}
					isOptionEqualToValue={(option, value) => Number(option?.site_line_id) === Number(value?.site_line_id)}
					onChange={(event, newValue) => {
						setSelectedLine(newValue);
						setAssignedMachines(applyProvisionFilters(scopedAllProvisionedMachines, selectedCustomer, selectedSiteData, newValue));
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Select Line"
							variant="outlined"
						/>
					)}
					fullWidth
					size="small"
						disabled={!selectedSiteData || filterLines.length === 0}
				/>
				</Box>
			</Box>

			<Box sx={{ px: 1 }}>
				<Typography variant="h6">
					{selectedSiteData
						? `${t('UI_TEXT.PROVISIONED_MACHINES')} ${selectedSiteData.customer_site_name}`
						: selectedCustomer
							? `${t('UI_TEXT.PROVISIONED_MACHINES')} ${selectedCustomer.customer_name}`
							: `${t('MENU_ITEMS.ALL')} ${t('UI_TEXT.PROVISIONED_MACHINES')}`}
				</Typography>

				<Datagrid
					data={assignedMachines}
					columns={columns}
					customButtons={customButtons}
					checkboxSelection={true}
					rowIdField="provision_row_id"
					onSelect={handleRowSelect}
					sortBy={[{ field: "machine_name", sort: "asc" }]}
					hideFilterFromColumnMenu
					lockActiveColumnInManageColumns
					preventHidingLastColumn
				/>

					<ModalWrapper open={newProvision}
						setOpen={setNewProvision}
						onClose={() => {
							resetProvisionForm();
							setNewProvision(false);
						}}
						title={t('UI_TEXT.ADD_NEW_PROVISION')}
						content={
							<>
								{provisionServerError && (
									<Alert severity="error" sx={{ width: "100%" }}>
										{provisionServerError}
									</Alert>
								)}
								<Box
									py={2}
									px={1}
								display="flex"
								flexDirection="column"
								gap={2}
								justifyContent="flex-start"
								alignItems="flex-start"
									style={{ width: "100%", minHeight: "350px" }}>
								<Autocomplete
									sx={{ width: "100%" }}
										options={scopedCustomers}
										getOptionLabel={(option) => option.customer_name}
										value={provisionCustomer}
										isOptionEqualToValue={(option, value) => Number(option?.cus_id) === Number(value?.cus_id)}
										onChange={(event, newValue) => {
											setProvisionCustomer(newValue);
											setProvisionSite(null);
											setProvisionLine(null);
											setProvisionLines([]);
											updateProvisionSites(newValue);
											setProvisionFormData((prev) => ({
												...prev,
												cus_id: newValue?.cus_id || "",
												cus_number: newValue?.customer_number || "",
												cus_name: newValue?.customer_name || "",
												site_id: "",
												site_number: "",
												site_name: "",
												site_line_id: "",
												line_name: "",
											}));
											setValidationErrors((prev) => ({ ...prev, cus_id: "", site_id: "", site_line_id: "" }));
										}}
										filterOptions={customerSiteFilterOptions}
										renderInput={(params) => (
											<TextField
												{...params}
												label={t('LABELS.SELECT_A_CUSTOMER')}
												variant="outlined"
												size="small"
												error={Boolean(validationErrors.cus_id)}
												helperText={validationErrors.cus_id || ""}
											/>
										)}
									/>
									<Autocomplete
										sx={{ width: "100%" }}
										options={provisionSites}
										getOptionLabel={getProvisionSiteOptionLabel}
										value={provisionSite}
										isOptionEqualToValue={isSameCustomerSite}
										onChange={(event, newValue) => {
											setProvisionSite(newValue);
											setProvisionLine(null);
											setProvisionLines([]);
											if (newValue && !provisionCustomer) {
												const parentCustomer = scopedCustomers.find((customer) => Number(customer?.cus_id) === Number(newValue?.cus_id)) || null;
												setProvisionCustomer(parentCustomer);
												updateProvisionSites(parentCustomer);
												setProvisionFormData((prev) => ({
													...prev,
													cus_id: parentCustomer?.cus_id || "",
													cus_number: parentCustomer?.customer_number || "",
													cus_name: parentCustomer?.customer_name || "",
												}));
											}
											setProvisionFormData((prev) => ({
												...prev,
												site_id: newValue?.site_id || "",
												site_number: newValue?.customer_site_number || "",
												site_name: newValue?.customer_site_name || "",
												site_line_id: "",
												line_name: "",
											}));
											setValidationErrors((prev) => ({ ...prev, site_id: "", site_line_id: "" }));
											loadProvisionLines(newValue, provisionCustomer || scopedCustomers.find((customer) => Number(customer?.cus_id) === Number(newValue?.cus_id)));
										}}
										filterOptions={customerSiteFilterOptions}
										renderInput={(params) => (
											<TextField
												{...params}
												label={t('LABELS.SELECT_CUSTOMER_SITE')}
												variant="outlined"
												size="small"
												error={Boolean(validationErrors.site_id)}
												helperText={validationErrors.site_id || ""}
											/>
										)}
										fullWidth
										size="small"
										disabled={provisionSites.length === 0}
									/>
									<Autocomplete
										sx={{ width: "100%" }}
										options={provisionLines}
										getOptionLabel={(option) => option?.line_name || ""}
										value={provisionLine}
										isOptionEqualToValue={(option, value) => Number(option?.site_line_id) === Number(value?.site_line_id)}
										onChange={(event, newValue) => {
											setProvisionLine(newValue);
											setProvisionFormData((prev) => ({
												...prev,
												site_line_id: newValue?.site_line_id || "",
												line_name: newValue?.line_name || "",
											}));
											setValidationErrors((prev) => ({ ...prev, site_line_id: "" }));
										}}
										renderInput={(params) => (
											<TextField
												{...params}
												label="Select Line"
												variant="outlined"
												size="small"
												error={Boolean(validationErrors.site_line_id)}
												helperText={validationErrors.site_line_id || ""}
											/>
										)}
										fullWidth
										size="small"
										disabled={!provisionSite || provisionLines.length === 0}
									/>
									<Autocomplete
										sx={{ width: "100%" }}
									options={nonAssignedMachines}
									getOptionLabel={(option) => `${option.machine_number} - ${option.machine_name}`}
									renderInput={(params) => (
										<TextField {...params} label={t('LABELS.SELECT_MACHINE')} variant="outlined" size="small" />
									)}
										onChange={(event, value) => {
											if (value) {
												setProvisionServerError("");
												setProvisionFormData((prevData) => ({
													...prevData,
													machine_id: value.machine_id || "",
													machine_name: value.machine_name || "",
													machine_number: value.machine_number || "",
													provision_date: new Date().toISOString().split("T")[0],
												}));
												setValidationErrors({});
												setErrorMessage("");
												setMachineSelected(value);
											} else {
												setProvisionFormData((prevData) => ({
													...prevData,
													machine_id: "",
													machine_name: "",
													machine_number: "",
													machine_serial_number: "",
													provision_date: "",
												}));
												setMachineSelected(null);
											} // Assign the selected machine object
										}}
									isOptionEqualToValue={(option, value) =>
										option.machine_number === value?.machine_number
									}
								/>

								{machineSelected && (
									<>
										<Typography variant="body1" fontWeight="bold">
											{t('UI_TEXT.MACHINE_DETAILS')}:{" "}
										</Typography>
										<FormGroup row sx={{ width: "100%", columnGap: "10px" }}>
											<TextField
												label={t('TABLE_HEADERS.MACHINE_NAME')}
												size="small"
												fullWidth
												variant="outlined"
												name="machine_name"
												sx={{ flex: 1 }}
												value={provisionFormData.machine_name}
												onChange={handleProvisionFormChange}
												disabled={!machineSelected}
												error={!!validationErrors.machine_name}
												helperText={validationErrors.machine_name || ""}
											/>

											{/* Machine Number */}
											<TextField
												label={t('TABLE_HEADERS.MACHINE_NUMBER')}
												size="small"
												fullWidth
												variant="outlined"
												name="machine_number"
												sx={{ flex: 1 }}
												value={provisionFormData.machine_number}
												onChange={handleProvisionFormChange}
												disabled
											/>

											{/* Machine Serial Number */}
											<TextField
												label={t('LABELS.MACHINE_SERIAL_NUMBER')}
												size="small"
												fullWidth
												variant="outlined"
												name="machine_serial_number"
												sx={{ flex: 1 }}
												value={provisionFormData.machine_serial_number}
												onChange={handleProvisionFormChange}
												disabled={!machineSelected}
												error={!!validationErrors.machine_serial_number}
												helperText={validationErrors.machine_serial_number || ""}
											/>
										</FormGroup>

										<Typography variant="body1" fontWeight="bold">
											{t('LABELS.PROVISION_DATE')}:{" "}
										</Typography>
										<TextField
											label={t('LABELS.PROVISION_DATE')}
											type="date"
											size="small"
											fullWidth
											InputLabelProps={{ shrink: true }}
											variant="outlined"
											name="provision_date"
											value={provisionFormData.provision_date}
											onChange={handleProvisionFormChange}
											disabled={!machineSelected}
											error={!!validationErrors.provision_date}
											helperText={validationErrors.provision_date || ""}
										/>
										<Typography variant="body1" fontWeight="bold">
											{UI_TEXT.PROVISION_FOR}:{" "}
										</Typography>
										<FormGroup row sx={{ width: "100%", columnGap: "10px" }}>
											<TextField
												label={t('TABLE_HEADERS.CUSTOMER_NAME')}
												size="small"
												fullWidth
												variant="outlined"
												name="cus_name"
												sx={{ flex: 1 }}
												disabled
												value={provisionFormData.cus_name}
												onChange={handleProvisionFormChange}
											/>

											<TextField
												label={t('LABELS.SITE_NAME')}
												size="small"
												fullWidth
												variant="outlined"
												name="site_name"
												sx={{ flex: 1 }}
												disabled
												value={provisionFormData.site_name}
												onChange={handleProvisionFormChange}
											/>
										</FormGroup>

										<Typography variant="body1" fontWeight="bold">
											{t('UI_TEXT.PROVISION_STATUS')}{" "}
										</Typography>
										<FormControl size="small" fullWidth disabled={!machineSelected}>
											<InputLabel>{t('TABLE_HEADERS.STATUS')}</InputLabel>
											<Select
												label={t('TABLE_HEADERS.STATUS')}
												name="status"
												value={provisionFormData.status}
												onChange={handleProvisionFormChange}>
												<MenuItem value="active">{t('MENU_ITEMS.ACTIVE')}</MenuItem>
												<MenuItem value="inactive">{t('MENU_ITEMS.INACTIVE')}</MenuItem>
											</Select>
										</FormControl>
									</>
								)}
							</Box>
							{errorMessage && (
								<Typography color="error" textAlign="center" marginBottom={2}>
									{errorMessage}
								</Typography>
							)}

							</>
						} actions={<>
								<Button
									variant="outlined"
									onClick={() => {
										resetProvisionForm();
										setNewProvision(false);
									}}>
									{t('BUTTON.CANCEL')}
								</Button>
								<Button
									variant="contained"
									onClick={handleProvisionFormSubmit}
									disabled={!machineSelected}>
									{submittingProvision ? (
										<>
											<CircularProgress size={16} sx={{ color: "white", marginRight: 1 }} />{" "}
											{t('BUTTON.SAVING')}
										</>
									) : (
										t('BUTTON.SUBMIT_PROVISION')
									)}
								</Button>
						</>} />


					<ModalWrapper open={editProvision}
						setOpen={setEditProvision}
						onClose={() => setEditProvision(false)}
						title={"Edit Provision"}
						content={<>
							<Box display="flex" flexDirection="column" gap={2} px={1} py={2} justifyContent="flex-start" alignItems="flex-start" style={{ width: "100%", minHeight: "250px" }}>
								<TextField
									label={t('TABLE_HEADERS.MACHINE_NAME')}
									size="small"
									fullWidth
									variant="outlined"
									name="machine_name"
									sx={{ flex: 1 }}
									value={editFormData.machine_name}
									onChange={handleEditFieldChange}
									disabled
								/>
								<TextField
									label={t('TABLE_HEADERS.MACHINE_NUMBER')}
									size="small"
									fullWidth
									variant="outlined"
									name="machine_number"
									sx={{ flex: 1 }}
									value={editFormData.machine_number}
									onChange={handleEditFieldChange}
									disabled
								/>

								<TextField
									label={t('TABLE_HEADERS.LINE', { defaultValue: 'Line' })}
									size="small"
									fullWidth
									variant="outlined"
									name="line_name"
									sx={{ flex: 1 }}
									value={editFormData.line_name}
									onChange={handleEditFieldChange}
									disabled
								/>

								<TextField
									label={t('LABELS.MACHINE_SERIAL_NUMBER')}
									size="small"
									fullWidth
									variant="outlined"
									name="machine_serial_number"
									sx={{ flex: 1 }}
									value={editFormData.machine_serial_number}
									onChange={handleEditFieldChange}
									error={Boolean(editValidationErrors.machine_serial_number)}
									helperText={editValidationErrors.machine_serial_number || ""}
								/>
								<Typography variant="body1" fontWeight="bold" >
									{t('UI_TEXT.PROVISION_STATUS')} {" "}
								</Typography>
								<FormControl size="small" fullWidth>
									<InputLabel>{t('TABLE_HEADERS.STATUS')}</InputLabel>
									<Select
										label={t('TABLE_HEADERS.STATUS')}
										name="status"
										value={editFormData.status}
										onChange={handleEditFieldChange}>
										<MenuItem value={t('MENU_ITEMS.ACTIVE')}>{t('MENU_ITEMS.ACTIVE')}</MenuItem>
										<MenuItem value= {t('MENU_ITEMS.INACTIVE')}>{t('MENU_ITEMS.INACTIVE')}</MenuItem>
									</Select>
								</FormControl>

								<Typography variant="body1" fontWeight="bold">
									{t('UI_TEXT.PROVISION_FOR')}{" "}
								</Typography>
								<TextField
									label={t('TABLE_HEADERS.CUSTOMER_NAME')}
									size="small"
									fullWidth
									variant="outlined"
									name="cus_name"
									sx={{ flex: 1 }}
									disabled
									value={editFormData.cus_name || selectedCustomer?.customer_name || ""}
								/>

								<TextField
									label={t('LABELS.SITE_NAME')}
									size="small"
									fullWidth
									variant="outlined"
									name="site_name"
									sx={{ flex: 1 }}
									disabled
									value={editFormData.site_name || selectedSiteData?.customer_site_name || ""}
								/>

								{editError && (
									<Typography color="error" variant="body2">
										{editError}
									</Typography>
								)}
							</Box>
						</>}
						actions={<>
							<Button variant="outlined" onClick={() => setEditProvision(false)}>
								{t('BUTTON.CANCEL')}
							</Button>
							<Button variant="contained" onClick={submitEditProvision} disabled={editSubmitting}>
								{editSubmitting ? (
									<>
										<CircularProgress size={16} sx={{ color: "white", marginRight: 1 }} /> {t('BUTTON.SAVING')}
									</>
								) : (
									t("BUTTON.SAVE_CHANGES")
								)}
							</Button>
						</>}

					/>
			</Box>
		</Box>
	);
};

export default ManageSiteMachines;
